import { interpolate } from '../lib/content';
import type { LocalProgress, Mode, UiCopy } from '../lib/types';

interface RoleSelectProps {
  ui: UiCopy;
  updatedAt: string;
  progress: LocalProgress;
  explain: boolean;
  onToggleExplain: () => void;
  onSelect: (mode: Mode) => void;
}

const roles: Mode[] = ['student', 'teacher', 'professional'];

export function RoleSelect({
  ui,
  updatedAt,
  progress,
  explain,
  onToggleExplain,
  onSelect,
}: RoleSelectProps) {
  const freshness = ui.assessment.freshness
    ? interpolate(ui.assessment.freshness, { date: updatedAt.slice(0, 10) })
    : '';
  const progressNote = progress.attempts > 0 && ui.assessment.progressNote
    ? interpolate(ui.assessment.progressNote, {
      attempts: progress.attempts,
      best: progress.bestScore,
    })
    : '';

  return (
    <main className="entry">
      <section className="entry-poster">
        <p className="eyebrow">{ui.assessment.kicker}</p>
        <h1>{ui.assessment.title}</h1>
        {ui.assessment.subtitle ? <p className="entry-lede">{ui.assessment.subtitle}</p> : null}
      </section>

      <section
        className="entry-roles"
        aria-labelledby={ui.assessment.chooseMode ? 'entry-roles-title' : undefined}
        aria-label={ui.assessment.chooseMode ? undefined : ui.assessment.title}
      >
        {ui.assessment.chooseMode || ui.assessment.roleNote ? (
          <div className="entry-roles-head">
            {ui.assessment.chooseMode ? <h2 id="entry-roles-title">{ui.assessment.chooseMode}</h2> : null}
            {ui.assessment.roleNote ? <span>{ui.assessment.roleNote}</span> : null}
          </div>
        ) : null}

        <ul className="role-list">
          {roles.map((role, index) => (
            <li key={role}>
              <button type="button" onClick={() => onSelect(role)}>
                <span className="role-index">0{index + 1}</span>
                <span className="role-text">
                  <strong>{ui.modes[role].label}</strong>
                  <small>{ui.modes[role].description}</small>
                </span>
                {ui.assessment.roleCount ? <span className="role-count">{ui.assessment.roleCount}</span> : null}
                <span className="role-go" aria-hidden="true">→</span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {ui.assessment.explainLabel ? (
        <button
          type="button"
          className="entry-pref"
          role="switch"
          aria-checked={explain}
          onClick={onToggleExplain}
        >
          <span>{ui.assessment.explainLabel}</span>
          <em data-on={explain}>{explain ? ui.assessment.on : ui.assessment.off}</em>
        </button>
      ) : null}

      {ui.assessment.anonymousNote ? <p className="entry-note">{ui.assessment.anonymousNote}</p> : null}

      {freshness || progressNote ? (
        <p className="entry-meta">
          {progressNote ? <b>{progressNote}</b> : null}
          {freshness ? <span>{freshness}</span> : null}
          {ui.assessment.refreshNote ? <span>{ui.assessment.refreshNote}</span> : null}
        </p>
      ) : null}
    </main>
  );
}
